import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  Minus,
  Pause,
  Play,
  Plus,
  Trash2,
  Wallet,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Card,
  Dialog,
  EmptyState,
  SearchInput,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney, plural } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import { catalogueRepository } from "@/repositories/catalogueRepository";
import { heldSaleRepository, type HeldSale } from "@/repositories/heldSaleRepository";
import { localSaleRepository } from "@/repositories/localSaleRepository";
import { modifiersRepository } from "@/repositories/modifiersRepository";
import {
  salesRepository,
  type PaymentMethod,
  type Sale,
  type TillProduct,
} from "@/repositories/salesRepository";
import { ShiftBar } from "@/features/shifts/ShiftBar";
import { useAuthStore } from "@/stores/authStore";
import { useSyncStore } from "@/stores/syncStore";

import { ModifierDialog } from "./ModifierDialog";
import { OfflineRefunds } from "./OfflineRefunds";
import { PaymentDialog } from "./PaymentDialog";
import { ReceiptDialog } from "./ReceiptDialog";
import { useBasket, type BasketLine } from "./useBasket";

/**
 * The till.
 *
 * Two halves: what you can sell on the left, what you are selling on the right.
 * The right-hand panel is the one a cashier watches, so it never moves, never
 * reflows, and shows the total in the largest type on the screen.
 *
 * Keyboard shortcuts are from the design — F9 to pay, F10/F11/F12 for the tender
 * — because a busy till is driven by a keyboard far more than by a finger, and a
 * cashier's hand does not leave the counter.
 */
export function SalesPage() {
  const client = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const can = useAuthStore((s) => s.can);
  const refreshCounts = useSyncStore((s) => s.refreshCounts);
  const drain = useSyncStore((s) => s.drain);

  const basket = useBasket(session?.user.id);
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState("");
  const [paying, setPaying] = useState<PaymentMethod | null>(null);
  const [asking, setAsking] = useState<TillProduct | null>(null);
  const [receipt, setReceipt] = useState<(Sale & { queued?: boolean }) | null>(
    null,
  );

  const debouncedTerm = useDebounced(term, 200);
  const scope = session?.activeSectionId ?? "all";

  const tills = useQuery({
    queryKey: ["sales", "tills", scope],
    queryFn: salesRepository.tills,
  });
  const categories = useQuery({
    queryKey: ["catalogue", "categories"],
    queryFn: catalogueRepository.categories,
  });
  const products = useQuery({
    queryKey: ["sales", "products", scope, debouncedTerm, category],
    queryFn: () =>
      salesRepository.gridProducts({
        q: debouncedTerm,
        category: category || undefined,
      }),
    placeholderData: (previous) => previous,
  });
  const summary = useQuery({
    queryKey: ["sales", "summary", scope],
    queryFn: () => salesRepository.summary(),
  });
  const reachable = useSyncStore((s) => s.reachable);
  const offline = reachable === false || (summary.isError && !summary.data);

  /**
   * The questions the till may have to ask — sizes, extras.
   *
   * Fetched whole and cached hard: a shop has a handful of groups, they change
   * when somebody edits the menu rather than during a lunch rush, and looking
   * them up per tap would put a round trip between a cashier and a kota.
   */
  const modifierGroups = useQuery({
    queryKey: ["modifier-groups"],
    queryFn: modifiersRepository.all,
    staleTime: 10 * 60_000,
  });

  /** Which questions apply to a given product, if any. */
  const groupsFor = (productId: string) =>
    (modifierGroups.data ?? [])
      .filter((group) => group.products.includes(productId))
      .sort((a, b) => a.sort_order - b.sort_order);

  /**
   * One tap adds; a product with questions opens the dialog first.
   *
   * A dialog in front of every Coke would be intolerable at a counter that also
   * sells cooldrinks, so the check is per product rather than a mode.
   */
  function addProduct(product: TillProduct) {
    if (groupsFor(product.id).length > 0) {
      setAsking(product);
      return;
    }
    basket.add(product);
  }

  // A paired terminal knows its own till; a laptop has to be told. Falling back
  // to the first the cashier may use beats blocking the sale on a dropdown.
  const tillId = session?.device?.tillId ?? tills.data?.[0]?.id ?? "";

  /**
   * Completing a sale goes through the **local** repository, always — online or
   * not. It writes the sale to IndexedDB and queues the upload before it touches
   * the network, so a browser crash or a dead signal between the two can never
   * produce a sale that happened and left no trace.
   *
   * The cashier gets a receipt either way. Whether the server has heard about it
   * yet is the software's problem, and the sync centre's.
   */
  const complete = useMutation({
    mutationFn: (input: { method: PaymentMethod; tendered: number; customerId?: string }) =>
      localSaleRepository.complete({
        saleId: basket.saleId,
        // The same key for every retry of this basket. See `useBasket`.
        idempotencyKey: basket.idempotencyKey,
        tillId,
        receiptPrefix: tills.data?.find((t) => t.id === tillId)?.code ?? "T00",
        // The till's counter, where the stock comes off. From the cached till
        // list offline; absent only on a till that has never had one.
        ...(tills.data?.find((t) => t.id === tillId)?.section_id
          ? { sectionId: tills.data.find((t) => t.id === tillId)!.section_id }
          : {}),
        lines: basket.lines.map((line) => ({
          productId: line.product.id,
          // The name a receipt shows, extras and all.
          name: line.modifiers.length
            ? `${line.product.name} (${line.modifiers.map((m) => m.name).join(", ")})`
            : line.product.name,
          quantity: line.quantity,
          unitPrice: line.unitPrice.toFixed(2),
          taxRate: line.product.tax_rate,
          modifierIds: line.modifiers.map((modifier) => modifier.id),
        })),
        method: input.method,
        tendered: input.tendered,
        total: basket.totals.subtotal,
        ...(input.customerId ? { customerId: input.customerId } : {}),
      }),
    onSuccess: ({ sale, queued }) => {
      setPaying(null);
      setReceipt({ ...sale, queued });
      basket.clear();
      void refreshCounts();
      // A queued sale should be on its way before the cashier has finished
      // handing over the change, not on the next minute's heartbeat.
      if (queued) void drain();
      void client.invalidateQueries({ queryKey: ["sales"] });
      void client.invalidateQueries({ queryKey: ["inventory"] });
      // The shift bar shows cash taken and a sale count. Without this it sat at
      // "R 0.00 · 0 sales" through a whole morning's trading, which reads as the
      // shift having failed to attach — found by selling something and looking.
      void client.invalidateQueries({ queryKey: ["shifts"] });
    },
  });

  // Held on this till, so Hold works with no signal and the basket can be
  // picked up again — see `heldSaleRepository`.
  const held = useQuery({
    queryKey: ["held-sales"],
    queryFn: () => heldSaleRepository.list(),
  });
  const hold = useMutation({
    mutationFn: () =>
      heldSaleRepository.hold({
        lines: basket.lines,
        total: basket.totals.subtotal,
        itemCount: basket.totals.unitCount,
      }),
    onSuccess: () => {
      basket.clear();
      void client.invalidateQueries({ queryKey: ["held-sales"] });
    },
  });
  const resume = useMutation({
    mutationFn: (id: string) => heldSaleRepository.resume<BasketLine>(id),
    onSuccess: (lines) => {
      if (lines) basket.load(lines);
      void client.invalidateQueries({ queryKey: ["held-sales"] });
    },
  });
  const discard = useMutation({
    mutationFn: (id: string) => heldSaleRepository.discard(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["held-sales"] }),
  });

  // ── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (paying || receipt || basket.lines.length === 0) return;

      const shortcut: Record<string, PaymentMethod> = {
        F9: "CASH",
        F10: "CASH",
        F11: "CARD",
        F12: "MOBILE",
      };
      const method = shortcut[event.key];
      if (method) {
        event.preventDefault();
        setPaying(method);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [paying, receipt, basket.lines.length]);

  const canSell = can("sales.create");
  // SmartSpaza support, looking at this shop's till to help with it. They see
  // what the cashier sees — the grid, its prices, the recent receipts — and
  // nothing that would ring up, hold or pay, which the server refuses anyway.
  const watching = Boolean(session?.supportView);

  if (!canSell && !watching) {
    return (
      <EmptyState
        title="You are not set up to sell"
        description="Ask a manager to give you the sales permission."
      />
    );
  }

  return (
    <>
      {/* Whose drawer this is. Above everything, because the commonest cash-up
          dispute is not arithmetic — it is two people having used one drawer. */}
      {watching ? (
        <p
          role="note"
          className="mb-4 rounded-card bg-info-50 px-4 py-3 text-sm text-ink-900"
        >
          <strong>Support view of the till.</strong> This is what the shop's
          cashiers see: their products, prices and recent sales. Open a receipt
          to see it in full. Nothing can be sold or changed from here.
        </p>
      ) : (
        <div className="mb-4">
          <ShiftBar
            tillId={tillId}
            tillName={tills.data?.find((t) => t.id === tillId)?.name ?? ""}
          />
        </div>
      )}

      <div
        className={cn(
          "grid gap-4",
          !watching && "xl:grid-cols-[minmax(0,1fr)_32rem]",
        )}
      >
        {/* ── What you can sell ────────────────────────────────────────────── */}
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Search product by name or barcode"
              containerClassName="w-full flex-1 sm:w-auto sm:min-w-[14rem]"
              autoFocus
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            <CategoryChip
              active={category === ""}
              onClick={() => setCategory("")}
            >
              All
            </CategoryChip>
            {categories.data?.map((item) => (
              <CategoryChip
                key={item.id}
                active={category === item.id}
                onClick={() => setCategory(category === item.id ? "" : item.id)}
              >
                {item.name}
              </CategoryChip>
            ))}
          </div>

          {products.isLoading ? (
            <SkeletonRows rows={6} />
          ) : products.data && products.data.length > 0 ? (
            // Capped and scrolled rather than allowed to run: a shop with two
            // hundred lines pushed Recent sales off the bottom of the screen, so
            // a cashier checking the last receipt had to scroll the whole page
            // past the grid to reach it. The cap keeps the till's furniture where
            // it was, and cuts a row in half so the grid reads as scrollable.
            <div className="grid max-h-[32rem] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
              {products.data.map((product) => (
                <ProductTile
                  key={product.id}
                  product={product}
                  onAdd={() => {
                    if (!watching) addProduct(product);
                  }}
                  hasChoices={groupsFor(product.id).length > 0}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title="Nothing matched"
              description="Try fewer letters, or scan the barcode."
            />
          )}

          {/* No signal: Recent sales needs the server, and the refunds a till may
              give offline are its own cash sales, read from its own record. */}
          {offline && tillId ? (
            <OfflineRefunds tillId={tillId} />
          ) : (
            <RecentSales rows={summary.data?.recent ?? []} onOpen={setReceipt} />
          )}
        </div>

        {/* ── What you are selling ─────────────────────────────────────────── */}
        {!watching && (
        <div id="current-sale" className="scroll-mt-4">
          <CurrentSale
            basket={basket}
            onHold={() => hold.mutate()}
            canHold={can("sales.hold")}
            held={held.data ?? []}
            onResume={(id) => resume.mutate(id)}
            onDiscard={(id) => discard.mutate(id)}
            onPay={(method) => setPaying(method)}
          />
        </div>
        )}
      </div>

      {/* ── The tender bar, on anything narrower than a till ────────────────
          Below `xl` the sale panel sits *under* the product grid, so on a phone
          Pay is forty products away. This keeps the total and the two tender
          buttons on screen wherever the cashier has scrolled to.

          Only when there is something to pay for: an empty bar covering the
          bottom of a product grid would cost a row of tiles for nothing. */}
      {basket.lines.length > 0 && (
        <div className="sticky bottom-0 -mx-4 mt-4 flex items-center gap-2 border-t border-line bg-surface/95 px-4 py-2.5 backdrop-blur sm:-mx-5 sm:px-5 xl:hidden">
          <a
            href="#current-sale"
            className="min-w-0 flex-1 text-left"
            aria-label="Go to the current sale"
          >
            <span className="block text-xs text-ink-500">
              {plural(basket.lines.length, "item")}
            </span>
            <span className="block text-base font-bold tabular-nums text-ink-900">
              {formatMoney(basket.totals.subtotal.toFixed(2))}
            </span>
          </a>

          <Button variant="money" onClick={() => setPaying("CASH")}>
            <Wallet className="size-4" aria-hidden /> Cash
          </Button>
          <Button variant="dark" onClick={() => setPaying("CARD")}>
            <CreditCard className="size-4" aria-hidden /> Card
          </Button>
        </div>
      )}

      {paying && !tillId && (
        // Nothing to put the sale on: a machine not paired to a till that has
        // never had signal to learn the shop's tills. Refusing here, with the
        // way out, beats queuing a sale with no till that the server can only
        // reject once the customer has gone.
        <Dialog
          open
          onClose={() => setPaying(null)}
          title="This machine is not set up as a till yet"
          description="Pair it in Settings ▸ Tills & devices, or connect it to the internet once so it can learn the shop's tills. Then the sale can go through."
          footer={
            <Button variant="primary" onClick={() => setPaying(null)}>
              OK
            </Button>
          }
        >
          <p className="text-sm text-ink-600">The basket is kept.</p>
        </Dialog>
      )}

      {paying && tillId && (
        <PaymentDialog
          due={basket.totals.subtotal}
          method={paying}
          pending={complete.isPending}
          error={complete.error}
          onClose={() => setPaying(null)}
          onPay={(method, tendered, customerId) =>
            complete.mutate({ method, tendered, ...(customerId ? { customerId } : {}) })
          }
          allowAccount={can("customers.view")}
        />
      )}

      {asking && (
        <ModifierDialog
          product={asking}
          groups={groupsFor(asking.id)}
          onClose={() => setAsking(null)}
          onConfirm={(modifiers) => {
            basket.add(asking, 1, modifiers);
            setAsking(null);
          }}
        />
      )}

      {receipt && (
        <ReceiptDialog
          sale={receipt}
          sectionId={tills.data?.find((t) => t.id === tillId)?.section_id}
          tillId={tillId || undefined}
          onClose={() => setReceipt(null)}
        />
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// The grid
// ═════════════════════════════════════════════════════════════════════════════
/**
 * One product on the grid.
 *
 * Proportioned from the supplied design, and the proportions are the point: the
 * picture gets a **square** of the tile's full width on white, so a tall bottle
 * and a wide bread bag both read at a glance from an arm's length away. The
 * first version gave it a short letterboxed strip on the grey page colour, which
 * made every photograph look like a mistake — a 500 ml Coke reduced to a
 * thumbnail floating in a grey bar.
 *
 * `object-contain`, never `cover`: a cropped pack shot loses the brand block,
 * which is the one part a cashier actually recognises.
 */
function ProductTile({
  product,
  onAdd,
  hasChoices,
}: {
  product: TillProduct;
  onAdd: () => void;
  /** Marked so a cashier knows a tap opens a question rather than adding. */
  hasChoices?: boolean;
}) {
  const initials = product.name
    .split(/\s+/)
    .filter((word) => /[a-z0-9]/i.test(word[0] ?? ""))
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");

  let hue = 0;
  for (const character of product.name)
    hue = (hue * 31 + character.charCodeAt(0)) % 360;

  // A made-up product has no balance of its own — a kota was never stock — so
  // the grid asks how many the ingredients allow instead. Showing "Out of stock"
  // for something the shop can make twelve of would stop a cashier selling it.
  const made = product.makeable == null ? null : Number(product.makeable);
  const onHand = made ?? Number(product.stock_on_hand);

  return (
    <button
      type="button"
      onClick={onAdd}
      className="relative flex flex-col overflow-hidden rounded-card border border-line bg-surface text-left transition-shadow hover:shadow-card active:scale-[0.99]"
    >
      {/* A tap on this one opens a question rather than adding straight away.
          Worth marking: a cashier who expects an instant add and gets a dialog
          taps twice, and the second tap lands on whatever the dialog put there. */}
      {hasChoices && (
        <span className="absolute right-1.5 top-1.5 z-10 rounded-full bg-brand-500 px-1.5 py-0.5 text-[0.625rem] font-semibold text-white">
          Choices
        </span>
      )}
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          // Lazy, because a grid of forty products on a prepaid bundle should
          // fetch what is on screen rather than what is scrolled past.
          loading="lazy"
          // A faint ground rather than nothing. The pack shots are cut out, so
          // this shows *through* them — and it means a tile whose picture has
          // not arrived yet reads as an image still loading rather than as an
          // empty card. Watching the grid come up over the dev proxy, the blank
          // white version looked broken for several seconds.
          className="aspect-[4/3] w-full bg-canvas object-contain p-2"
        />
      ) : (
        <span
          className="grid aspect-[4/3] w-full place-items-center text-xl font-semibold"
          style={{
            backgroundColor: `hsl(${hue} 70% 94%)`,
            color: `hsl(${hue} 55% 32%)`,
          }}
          aria-hidden
        >
          {initials}
        </span>
      )}

      <span className="flex flex-1 flex-col gap-0.5 px-2.5 pb-2.5 pt-2">
        <span className="line-clamp-2 text-sm font-medium leading-snug text-ink-900">
          {product.name}
        </span>
        <span className="mt-auto text-base font-semibold text-brand-600">
          {formatMoney(product.selling_price)}
        </span>
        <span
          className={cn(
            "text-xs",
            onHand <= 0 ? "text-danger-700" : "text-ink-500",
          )}
        >
          {made != null
            ? made <= 0
              ? "Ingredients short"
              : `Can make ${formatQuantity(made)}`
            : onHand <= 0
              ? "Out of stock"
              : `In stock: ${formatQuantity(onHand)}`}
        </span>
      </span>
    </button>
  );
}

/** Whole numbers stay whole. "In stock: 48", not "In stock: 48.000". */
function formatQuantity(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(3).replace(/0+$/, "");
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "h-9 rounded-full border px-3.5 text-sm transition-colors",
        active
          ? "border-brand-500 bg-brand-500 font-medium text-white"
          : "border-line text-ink-600 hover:text-ink-900",
      )}
    >
      {children}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// The panel
// ═════════════════════════════════════════════════════════════════════════════
function CurrentSale({
  basket,
  onHold,
  canHold,
  held,
  onResume,
  onDiscard,
  onPay,
}: {
  basket: ReturnType<typeof useBasket>;
  onHold: () => void;
  canHold: boolean;
  held: HeldSale[];
  onResume: (id: string) => void;
  onDiscard: (id: string) => void;
  onPay: (method: PaymentMethod) => void;
}) {
  const empty = basket.lines.length === 0;
  const due = basket.totals.subtotal;
  const [showHeld, setShowHeld] = useState(false);

  return (
    <Card className="flex h-fit flex-col xl:sticky xl:top-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink-900">Current sale</h2>
        <div className="flex gap-1">
          {held.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowHeld(true)}>
              <Play className="size-4" aria-hidden /> Held ({held.length})
            </Button>
          )}
          {canHold && (
            <Button variant="ghost" size="sm" disabled={empty} onClick={onHold}>
              <Pause className="size-4" aria-hidden /> Hold
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={empty}
            onClick={basket.clear}
          >
            <Trash2 className="size-4 text-danger-700" aria-hidden /> Clear
          </Button>
        </div>
      </div>

      {empty ? (
        <p className="py-12 text-center text-sm text-ink-500">
          Tap a product, or scan a barcode, to start.
        </p>
      ) : (
        <ul className="mt-3 max-h-[22rem] divide-y divide-line overflow-y-auto">
          {basket.lines.map((line) => (
            <li key={line.key} className="flex items-center gap-2 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-900">
                  {line.product.name}
                </p>
                {line.modifiers.length > 0 && (
                  <p className="truncate text-xs text-warn-700">
                    {line.modifiers.map((modifier) => modifier.name).join(", ")}
                  </p>
                )}
                <p className="text-xs text-ink-500">
                  {formatMoney(line.unitPrice.toFixed(2))} each
                </p>
              </div>

              <div className="flex items-center gap-1">
                <Stepper
                  label={`One less ${line.product.name}`}
                  onClick={() =>
                    basket.setQuantity(line.key, line.quantity - 1)
                  }
                >
                  <Minus className="size-3.5" aria-hidden />
                </Stepper>
                <span className="w-7 text-center text-sm font-semibold tabular-nums">
                  {line.quantity}
                </span>
                <Stepper
                  label={`One more ${line.product.name}`}
                  onClick={() =>
                    basket.setQuantity(line.key, line.quantity + 1)
                  }
                >
                  <Plus className="size-3.5" aria-hidden />
                </Stepper>
              </div>

              <span className="w-20 text-right text-sm font-semibold tabular-nums text-ink-900">
                {formatMoney((line.unitPrice * line.quantity).toFixed(2))}
              </span>

              <button
                type="button"
                onClick={() => basket.remove(line.key)}
                aria-label={`Remove ${line.product.name}`}
                className="grid size-7 place-items-center rounded-control text-danger-700 hover:bg-danger-50"
              >
                <X className="size-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-1.5 border-t border-line pt-4 text-sm">
        <Row label="Subtotal" value={formatMoney(due.toFixed(2))} />
        {/* No Tax row. The demo merchant is not VAT registered — confirmed open
            decision 2 — and a "Tax R0.00" line on every receipt would be noise
            that invites the question "why am I not charging VAT?". It appears
            the moment VAT is switched on in Settings. */}
      </div>

      <div className="mt-3 flex items-baseline justify-between border-t border-line pt-3">
        <span className="text-base font-semibold text-ink-900">Total</span>
        <span className="text-total tabular-nums text-brand-600">
          {formatMoney(due.toFixed(2))}
        </span>
      </div>

      <Button
        variant="money"
        size="lg"
        className="mt-4 w-full"
        disabled={empty}
        onClick={() => onPay("CASH")}
      >
        Pay (F9)
      </Button>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button
          variant="secondary"
          disabled={empty}
          onClick={() => onPay("CASH")}
        >
          <Wallet className="size-4" aria-hidden /> Cash (F10)
        </Button>
        <Button
          variant="secondary"
          disabled={empty}
          onClick={() => onPay("CARD")}
        >
          <CreditCard className="size-4" aria-hidden /> Card (F11)
        </Button>
      </div>
      {showHeld && (
        <Dialog
          open
          onClose={() => setShowHeld(false)}
          title="Held sales"
          description="Waiting on this till. Resume one to finish it, oldest first."
        >
          <ul className="divide-y divide-line">
            {held.map((sale) => (
              <li key={sale.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink-900">
                    Held at {sale.label}
                  </span>
                  <span className="block text-xs text-ink-500">
                    {plural(sale.itemCount, "item")} · {formatMoney(sale.total)}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!empty}
                  onClick={() => {
                    onResume(sale.id);
                    setShowHeld(false);
                  }}
                >
                  Resume
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDiscard(sale.id)}>
                  <Trash2 className="size-4 text-danger-700" aria-hidden />
                  <span className="sr-only">Discard</span>
                </Button>
              </li>
            ))}
          </ul>
          {!empty && (
            <p className="mt-2 text-sm text-warn-700">
              Finish or hold the sale on screen before resuming another.
            </p>
          )}
        </Dialog>
      )}
    </Card>
  );
}

function Stepper({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid size-7 place-items-center rounded-control border border-line text-ink-600 hover:text-ink-900"
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-ink-600">{label}</span>
      <span className="tabular-nums text-ink-900">{value}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Recent sales
// ═════════════════════════════════════════════════════════════════════════════
function RecentSales({
  rows,
  onOpen,
}: {
  rows: {
    id: string;
    receipt_number: string;
    total: string;
    item_count: number;
    payment_method: string;
    completed_at: string | null;
    status: string;
  }[];
  onOpen: (sale: Sale) => void;
}) {
  const open = useMutation({
    mutationFn: (id: string) => salesRepository.sale(id),
    onSuccess: onOpen,
  });

  // "Find a receipt": a customer back on Thursday with Monday's slip. The
  // number on the paper is what gets typed, and the match replaces the list.
  const [query, setQuery] = useState("");
  const find = useMutation({ mutationFn: salesRepository.findByReceipt });
  const found = find.data?.map((sale) => ({
    id: sale.id,
    receipt_number: sale.receipt_number,
    total: sale.total,
    item_count: sale.item_count,
    payment_method: sale.payments[0]?.method ?? "",
    completed_at: sale.completed_at,
    status: sale.status,
  }));

  const visible = useMemo(() => (found ?? rows).slice(0, 5), [found, rows]);

  return (
    <Card flush className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink-900">
          {found ? "Matching receipts" : "Recent sales"}
        </h2>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (query.trim()) find.mutate(query);
          }}
        >
          <label htmlFor="find-receipt" className="sr-only">
            Receipt number
          </label>
          <input
            id="find-receipt"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (!event.target.value) find.reset();
            }}
            placeholder="Find a receipt"
            className="h-9 w-36 rounded-control border border-line bg-surface px-2.5 text-sm text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
          <Button type="submit" size="sm" variant="secondary" loading={find.isPending}>
            Find
          </Button>
        </form>
      </div>

      {find.error != null && (
        <p className="mt-2 text-sm text-danger-700">
          Searching needs signal. Try again when the till is online.
        </p>
      )}

      {visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-500">
          {found ? "No receipt with that number." : "Nothing sold yet today."}
        </p>
      ) : (
        <div className="mt-3">
          <Table>
            <THead>
              <TH>Receipt</TH>
              <TH className="text-right">Items</TH>
              <TH className="text-right">Total</TH>
              <TH>Payment</TH>
              <TH>Time</TH>
            </THead>
            <TBody>
              {visible.map((row) => (
                <TR
                  key={row.id}
                  className={cn(
                    "cursor-pointer",
                    row.status === "VOIDED" && "opacity-50",
                  )}
                >
                  <TD>
                    <button
                      type="button"
                      onClick={() => open.mutate(row.id)}
                      className="font-mono text-xs font-medium text-brand-600 hover:underline"
                    >
                      {row.receipt_number}
                    </button>
                    {row.status === "VOIDED" && (
                      <span className="ml-2 text-xs text-danger-700">
                        voided
                      </span>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums">{row.item_count}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMoney(row.total)}
                  </TD>
                  <TD className="text-sm capitalize">
                    {row.payment_method.toLowerCase()}
                  </TD>
                  <TD className="text-sm text-ink-500">
                    {row.completed_at
                      ? new Date(row.completed_at).toLocaleTimeString("en-ZA", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
